import { useCallback } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { ExtractedLeadV2, Lead } from "../../types/lead";
import { findMergeSuggestion } from "@/lib/lead/merge/duplicates";
import { updateLeadField } from "@/lib/lead/actions/updateLeadField";
import { mergeLeadIntoCurrentAction } from "@/lib/lead/actions/mergeLeadIntoCurrent";
import { reextractCurrentLeadAction } from "@/lib/lead/actions/reextractCurrentLead";
import { deleteLeadAction } from "@/lib/lead/actions/deleteLead";
import { loadLeadIntoEditor } from "@/lib/lead/actions/loadLeadIntoEditor";
import { mergeExtractedPreferNonEmpty } from "@/lib/lead/mergeExtractedPreferNonEmpty";
import { isMeaningful } from "@/lib/lead/utils/isMeaningful";
import { textHeuristics } from "@/lib/lead/extraction/textHeuristics";
import { sanitizeCustomerPhone, formatPhoneStrict } from "../../lib/lead/phones";
import { recordFieldEdit } from "@/lib/dev/devEditLog";

type MergeSuggestion = { sourceId: string; reason: string };

type UseLeadEditorActionsParams = {
  getScopedLeadsPool: () => Lead[];
  currentLeadRef: MutableRefObject<Lead | null>;
  transcriptRef: MutableRefObject<string>;
  listeningRef: MutableRefObject<boolean>;
  pendingSendLead: Lead | null;
  setPendingSendLead: Dispatch<SetStateAction<Lead | null>>;
  setDuplicateMatches: Dispatch<SetStateAction<Lead[]>>;
  setDuplicateReason: Dispatch<SetStateAction<string | null>>;
  setMergeSuggestion: Dispatch<SetStateAction<MergeSuggestion | null>>;
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  setCurrentLead: Dispatch<SetStateAction<Lead | null>>;
  setTranscript: Dispatch<SetStateAction<string>>;
  setIsListening: Dispatch<SetStateAction<boolean>>;
  extract: (text: string) => Promise<any>;
  purgeLeads: (ids: string[]) => Promise<any>;
  /**
   * Optional accessor for the authenticated user id. Used to stamp
   * `user_overrides` entries written by `updateLeadField` (transcript
   * merge v2). Falls back to null when omitted.
   */
  getActorUserId?: () => string | null;
  /**
   * Phase 2 of transcript merge v2: invoked by `reextractCurrentLead` when
   * the merge produces conflicts. Plumbed to the workspace shell which
   * opens `<LeadMergeConflictModal>`.
   */
  onMergeConflicts?: (args: {
    leadId: string;
    mergeResult: import("@/lib/lead/extraction/mergeLeadFromTranscript").MergeLeadResult;
    source: "reextract";
  }) => void;
};

export function useLeadEditorActions(params: UseLeadEditorActionsParams) {
  const findMergeSuggestionForCurrent = useCallback((): MergeSuggestion | null => {
    return findMergeSuggestion(params.currentLeadRef.current, params.getScopedLeadsPool(), {
      normalizePhone: textHeuristics.normalizePhone,
    });
  }, [params]);

  const updateCurrentLeadField = useCallback(
    (field: keyof ExtractedLeadV2, value: any) => {
      const lead = params.currentLeadRef.current;
      // L3 fix: do NOT bail when `lead.extracted` is null. A freshly created
      // "New Customer" lead has extracted=null by design; updateLeadField now
      // normalizes that to an empty extracted base so manual edits persist.
      if (!lead) return;
      const { updatedLead, shouldRecomputeMergeSuggestion } = updateLeadField(lead, field, value, {
        formatPhoneStrict,
        isMeaningful,
        shouldTriggerMergeSuggestion: (f) =>
          f === "phone" || f === "email" || f === "first_name" || f === "last_name",
        getActorUserId: params.getActorUserId,
        recordFieldEdit,
      });
      if (shouldRecomputeMergeSuggestion) {
        params.setMergeSuggestion(null);
        setTimeout(() => params.setMergeSuggestion(findMergeSuggestionForCurrent()), 0);
      }
      params.setCurrentLead(updatedLead);
      params.currentLeadRef.current = updatedLead;
      params.setLeads((prev) => prev.map((l) => (l.id === updatedLead.id ? updatedLead : l)));
    },
    [findMergeSuggestionForCurrent, params]
  );

  const mergeLeadIntoCurrent = useCallback(
    async (sourceId: string, opts?: { skipConfirm?: boolean }) => {
      await mergeLeadIntoCurrentAction(sourceId, opts, {
        getCurrentLead: () => params.currentLeadRef.current,
        getLeadsPool: params.getScopedLeadsPool,
        extract: params.extract,
        textHeuristics,
        isMeaningful,
        mergeExtractedPreferNonEmpty,
        setLeads: params.setLeads,
        setCurrentLead: params.setCurrentLead,
        setTranscript: params.setTranscript,
        transcriptRef: params.transcriptRef,
        setIsListening: params.setIsListening,
        listeningRef: params.listeningRef,
        setMergeSuggestion: params.setMergeSuggestion,
        findMergeSuggestionForCurrent,
        setPendingSendLead: params.setPendingSendLead,
        setDuplicateMatches: params.setDuplicateMatches,
        setDuplicateReason: params.setDuplicateReason,
        purgeLeads: params.purgeLeads,
      });
    },
    [findMergeSuggestionForCurrent, params]
  );

  const reextractCurrentLead = useCallback(async () => {
    await reextractCurrentLeadAction({
      extract: params.extract,
      textHeuristics,
      sanitizeCustomerPhone,
      getCurrentLead: () => params.currentLeadRef.current,
      setCurrentLead: params.setCurrentLead,
      setLeads: params.setLeads,
      currentLeadRef: params.currentLeadRef,
      onMergeConflicts: params.onMergeConflicts,
    });
  }, [params]);

  const deleteLead = useCallback((id: string) => {
    deleteLeadAction(id, {
      setLeads: params.setLeads,
      setCurrentLead: params.setCurrentLead,
      setTranscript: params.setTranscript,
      setIsListening: params.setIsListening,
      setMergeSuggestion: params.setMergeSuggestion,
      pendingSendLead: params.pendingSendLead,
      setPendingSendLead: params.setPendingSendLead,
      setDuplicateMatches: params.setDuplicateMatches,
      setDuplicateReason: params.setDuplicateReason,
      currentLeadRef: params.currentLeadRef,
      listeningRef: params.listeningRef,
    });
  }, [params]);

  const openLeadInEditor = useCallback(
    (lead: Lead) => {
      const reviewedLead = lead.needs_review ? { ...lead, needs_review: false, lastUpdated: Date.now() } : lead;
      loadLeadIntoEditor(reviewedLead, {
        setCurrentLead: params.setCurrentLead,
        setTranscript: params.setTranscript,
        setIsListening: params.setIsListening,
        setMergeSuggestion: params.setMergeSuggestion,
        currentLeadRef: params.currentLeadRef,
        transcriptRef: params.transcriptRef,
        listeningRef: params.listeningRef,
        findMergeSuggestionForCurrent,
      });
      if (reviewedLead !== lead) {
        params.setLeads((prev) => prev.map((l) => (l.id === reviewedLead.id ? reviewedLead : l)));
      }
    },
    [findMergeSuggestionForCurrent, params]
  );

  const updateFollowUpStatus = useCallback(
    (leadId: string, followUpId: string, status: "open" | "done" | "cancelled") => {
      params.setLeads((prev) =>
        prev.map((l) =>
          l.id !== leadId
            ? l
            : {
                ...l,
                follow_ups: (l.follow_ups ?? []).map((f) => (f.id === followUpId ? { ...f, status } : f)),
                lastUpdated: Date.now(),
              }
        )
      );
      if (params.currentLeadRef.current?.id === leadId) {
        const next = {
          ...params.currentLeadRef.current,
          follow_ups: (params.currentLeadRef.current.follow_ups ?? []).map((f) =>
            f.id === followUpId ? { ...f, status } : f
          ),
          lastUpdated: Date.now(),
        };
        params.currentLeadRef.current = next;
        params.setCurrentLead(next);
      }
    },
    [params]
  );

  const createFollowUp = useCallback(
    (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => {
      const nextAction = draft.next_action.trim();
      const dueDate = draft.due_date.trim();
      const reason = draft.reason.trim();
      if (!nextAction || !dueDate) return;
      const followUp = {
        id: `fup_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        next_action: nextAction,
        due_date: dueDate,
        reason,
        status: "open" as const,
      };
      params.setLeads((prev) =>
        prev.map((l) =>
          l.id !== leadId
            ? l
            : {
                ...l,
                follow_ups: [...(l.follow_ups ?? []), followUp],
                lastUpdated: Date.now(),
              }
        )
      );
      if (params.currentLeadRef.current?.id === leadId) {
        const next = {
          ...params.currentLeadRef.current,
          follow_ups: [...(params.currentLeadRef.current.follow_ups ?? []), followUp],
          lastUpdated: Date.now(),
        };
        params.currentLeadRef.current = next;
        params.setCurrentLead(next);
      }
    },
    [params]
  );

  const saveFollowUp = useCallback(
    (
      leadId: string,
      followUpId: string,
      patch: Partial<{ next_action: string; due_date: string; reason: string; status: "open" | "done" | "cancelled" }>
    ) => {
      params.setLeads((prev) =>
        prev.map((l) =>
          l.id !== leadId
            ? l
            : {
                ...l,
                follow_ups: (l.follow_ups ?? []).map((f) => (f.id === followUpId ? { ...f, ...patch } : f)),
                lastUpdated: Date.now(),
              }
        )
      );
      if (params.currentLeadRef.current?.id === leadId) {
        const next = {
          ...params.currentLeadRef.current,
          follow_ups: (params.currentLeadRef.current.follow_ups ?? []).map((f) =>
            f.id === followUpId ? { ...f, ...patch } : f
          ),
          lastUpdated: Date.now(),
        };
        params.currentLeadRef.current = next;
        params.setCurrentLead(next);
      }
    },
    [params]
  );

  return {
    findMergeSuggestionForCurrent,
    updateCurrentLeadField,
    mergeLeadIntoCurrent,
    reextractCurrentLead,
    deleteLead,
    openLeadInEditor,
    updateFollowUpStatus,
    createFollowUp,
    saveFollowUp,
  };
}
